<template>
  <section id="overview">
    <div class="row clearfix">
      <figure class="chart">
        <div class="callouts" v-if="activeReport">
          <aside id="overview-rev-total">
            <h2>
              $<ICountUp :endVal="activeReport.revenue" :options="{decimalPlaces: 1}"/>b
            </h2>
            <h3>Total Revenue </h3>
          </aside>
          <aside id="overview-rev-growth">
            <h2>
              <ICountUp :endVal="activeReport.revenueGrowth" :options="{decimalPlaces: 1}"/>%
            </h2>
            <h3>Revenue Growth</h3>
          </aside>
          <aside id="overview-att-total">
            <h2>
              <ICountUp :endVal="activeReport.tickets" :options="{decimalPlaces: 1}"/>m
            </h2>
            <h3>Total Attendance</h3>
          </aside>
          <aside id="overview-att-growth">
            <h2>
              <ICountUp :endVal="activeReport.ticketGrowth" :options="{decimalPlaces: 1}"/>%
            </h2>
            <h3>Attendance Growth</h3>
          </aside>
          <aside id="overview-avg-price">
            <h2>
              $<ICountUp :endVal="activeReport.avgTicketPrice" :options="{decimalPlaces: 2}"/>
            </h2>
            <h3>Avg Ticket Price</h3>
          </aside>
        </div>
      </figure>

      <article>
        <h1>Ticket Revenue and Attendance Overview</h1>
        <p>In 2025, the live performance industry recorded a marginal decline in revenue and attendance compared with 2024, with both remaining at their second-highest levels on record. Softer ticket sales and rising operating costs were partially offset by the continued return of major international touring acts and touring productions, as well as higher average ticket prices. Overall, the limited decline highlights the industry's resilience amid ongoing market and inflationary pressures.</p>
        <p><strong>2025 Results</strong></p>
        <ul>
          <li><strong>Revenue:</strong> In 2025, the Australian live performance industry generated total ticket sales revenue of $3.4b, a 0.1% decline from 2024 but a 70.6% increase from 2019 (pre-COVID-19). Performances in regional areas contributed $353.9m (10.6%) to total revenue.</li>
          <li><strong>Attendance:</strong> In 2025, 30.7m tickets were issued, a 2.4% decline from 2024 and a 28.7% increase from 2019. Of the total tickets issued, 28.4m were paid tickets (a decline from 29.1m tickets in 2024). The remaining 2.3m were complimentary, sponsor allocated or zero priced tickets. Performances in regional areas contributed 3.6m tickets (11.6%) to total attendance.</li>
          <li><strong>Average ticket price:</strong> The average ticket price increased by 2.4% from $115.12 in 2024 to $117.83 in 2025. This is the highest ever recorded for the industry.</li>
        </ul>
        <p><strong>Key State Insights</strong></p>
        <ul>
          <li>Among all states and territories, WA, SA, ACT and NT recorded y-o-y growth in revenue, with SA and ACT achieving their highest-ever revenue totals.</li>
          <li>In terms of attendance, WA, SA, ACT and TAS recorded y-o-y growth, with SA reaching its highest-ever attendance level.</li>
          <li>ACT recorded the highest y-o-y growth in revenue, followed by WA, while TAS recorded the highest y-o-y growth in attendance, followed by ACT and WA.</li>
          <li>As the two most populated states, NSW and VIC remain Australia’s major markets for live performance, collectively accounting for 68.1% of revenue and 62.8% of attendance in 2025.</li>
          <li>Among all states and territories, TAS recorded the highest proportion of revenue (60.6%) and attendance (47.5%) from performances in regional areas, followed by ACT (40.2% revenue and 24.8% attendance).</li>
        </ul>
        <p><strong>Key Category Insights</strong></p>
        <ul>
          <li>Among all categories, Circus and Physical Theatre, Classical Music, Festivals (Multi-Category), Festivals (Contemporary Music), and Opera recorded y-o-y growth in revenue and attendance. Among these, Classical Music, Festivals (Multi-Category) and Festivals (Contemporary Music) achieved their highest-ever revenue levels, while Festivals (Contemporary Music) also reached its highest-ever attendance level.</li>
          <li><strong>Circus and Physical Theatre</strong> recorded the highest y-o-y growth in revenue (159.6%) and attendance (88.1%) among all categories.*</li>
          <li><strong>Opera</strong> recorded the second-highest y-o-y growth in revenue (48.0%) and attendance (33.2%).</li>
          <li><strong>Contemporary Music</strong> and <strong>Musical Theatre</strong> were the two largest categories, generating 53.5% and 13.2% of total revenue, and 44.8% and 11.9% of total attendance, respectively.</li>
          <li>Combined, <strong>Contemporary Music</strong> and <strong>Festivals (Contemporary Music)</strong> contributed 65.5% of national revenue and 51.6% of national attendance.</li>
        </ul>
        <p style="font-size: .8em;">*Cirque du Soleil’s <em>Luzia</em> was presented in NSW in 2024; however, data for this event were not included in the 2024 report. In 2025, data for Cirque du Soleil’s <em>Corteo</em> have been included, which appears to be a major contributor to the significant growth observed in Circus and Physical Theatre.</p>
      </article>
    </div>

    <div class="fullbg">
      <p class="caption">Photo: <em>Night Night</em>, The Last Great Hunt, Photo Credit: Court McAllister</p>
    </div>
  </section>
</template>

<script>
export default {
  data () {
    return {
      activeYear: 2025,
      reports: [
        {
          year: 2024,
          revenue: 3.35,
          revenueGrowth: 6.9,
          tickets: 31.45,
          ticketGrowth: 4.6,
          avgTicketPrice: 115.12
        },
        {
          year: 2025,
          revenue: 3.351,
          revenueGrowth: -0.1,
          tickets: 30.705,
          ticketGrowth: -2.4,
          avgTicketPrice: 117.83
        }
      ]
    }
  },
  computed: {
    activeReport () {
      const self = this

      for (let i = 0; i < self.reports.length; i++) {
        const report = self.reports[i]
        if (report.year === self.activeYear) {
          return report
        }
      }

      return false
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
#overview .fullbg {
  background-image: url("~@/assets/img/2025/16-Category-Trends-250228_TLGH_Night-Night_1682-(2).jpg");
}
</style>
